import React, { useEffect, useState } from 'react';

import { Modal } from '@/components';
import { Button } from '@/design-system/button';
import { cn } from '@/design-system/lib/utils';
import { TableData, type TableDataColumn } from '@/design-system/table-data';

import { fmtQty } from '../format';
import type { ShortfallRow } from '../types';
import { FormField, TextArea } from './form';
import { codeCls, numCls } from './ui';

/* ---------------------------- disabled reason ---------------------------- */

/** Why an action is unavailable — visible text next to / under it. */
export const DisabledReason: React.FC<{
  children: React.ReactNode;
  testId?: string;
  className?: string;
}> = ({ children, testId, className }) => (
  <span
    className={cn('text-[12px] text-text-sub-600', className)}
    data-testid={testId}
  >
    {children}
  </span>
);

/* ------------------------------ action dialog ---------------------------- */

export interface ActionDialogProps {
  open: boolean;
  /** The action, e.g. "Submit shipping". */
  title: string;
  /** Document code(s) / selected count. */
  subtitle?: React.ReactNode;
  /** One short consequence line. */
  description?: React.ReactNode;
  /** Primary button — the action again. */
  confirmLabel: string;
  tone?: 'primary' | 'destructive';
  icon?: string;
  busy?: boolean;
  /** Keep the primary button off (a required input is empty, …). */
  confirmDisabled?: boolean;
  /** Shown in the footer while the primary button is off. */
  disabledReason?: React.ReactNode;
  width?: 'sm' | 'md' | 'lg';
  /** `data-testid` of the body; `-confirm` / `-reason` are derived from it. */
  testId?: string;
  onConfirm: () => void;
  onClose: () => void;
  /** Required inputs. */
  children?: React.ReactNode;
}

/**
 * The one confirm dialog of the WMS screens: title = the action, one
 * consequence line, the inputs the action needs, and a primary button named
 * after the action. Enter in a single-line input confirms.
 */
export const ActionDialog: React.FC<ActionDialogProps> = ({
  open,
  title,
  subtitle,
  description,
  confirmLabel,
  tone = 'primary',
  icon,
  busy,
  confirmDisabled,
  disabledReason,
  width = 'sm',
  testId = 'action-dialog',
  onConfirm,
  onClose,
  children,
}) => {
  const destructive = tone === 'destructive';
  const off = !!confirmDisabled;
  const confirm = () => {
    if (!off && !busy) onConfirm();
  };
  return (
    <Modal
      isOpen={open}
      onClose={onClose}
      icon={icon ?? (destructive ? 'ri-error-warning-line' : 'ri-check-line')}
      iconTone={destructive ? 'danger' : 'primary'}
      title={title}
      subtitle={subtitle}
      width={width}
      closeOnBackdropClick={!busy}
      closeOnEscape={!busy}
      footer={
        <div className="flex items-center justify-end gap-2">
          {off && disabledReason && (
            <DisabledReason testId={`${testId}-reason`} className="mr-auto">
              {disabledReason}
            </DisabledReason>
          )}
          <Button
            tone="neutral"
            style="stroke"
            onClick={onClose}
            disabled={busy}
          >
            Batal
          </Button>
          <Button
            tone={destructive ? 'destructive' : 'primary'}
            onClick={confirm}
            disabled={off}
            loading={busy}
            data-testid={`${testId}-confirm`}
          >
            {confirmLabel}
          </Button>
        </div>
      }
    >
      <form
        className="flex flex-col gap-3"
        data-testid={testId}
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          confirm();
        }}
      >
        {description && (
          <p className="text-sm text-text-sub-600">{description}</p>
        )}
        {children}
        <button type="submit" className="sr-only" tabIndex={-1} aria-hidden>
          {confirmLabel}
        </button>
      </form>
    </Modal>
  );
};

/* ------------------------------ reason dialog ---------------------------- */

interface ReasonModalProps {
  open: boolean;
  title: string;
  subtitle?: string;
  /** One short consequence line. */
  notice?: React.ReactNode;
  confirmLabel: string;
  placeholder?: string;
  busy?: boolean;
  /** Server validation message for the reason. */
  error?: string | null;
  testId?: string;
  onConfirm: (reason: string) => void;
  onClose: () => void;
}

/** Destructive action that requires a reason (cancel OO, cancel PO). */
export const ReasonModal: React.FC<ReasonModalProps> = ({
  open,
  title,
  subtitle,
  notice,
  confirmLabel,
  placeholder,
  busy,
  error,
  testId = 'reason-dialog',
  onConfirm,
  onClose,
}) => {
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (open) setReason('');
  }, [open]);

  const value = reason.trim();

  return (
    <ActionDialog
      open={open}
      title={title}
      subtitle={subtitle}
      description={notice}
      confirmLabel={confirmLabel}
      tone="destructive"
      icon="ri-close-circle-line"
      width="md"
      busy={busy}
      confirmDisabled={!value}
      disabledReason="Alasan wajib diisi"
      testId={testId}
      onConfirm={() => onConfirm(value)}
      onClose={onClose}
    >
      <FormField label="Alasan" htmlFor="wms-reason" required error={error}>
        <TextArea
          id="wms-reason"
          rows={3}
          value={reason}
          onChange={setReason}
          placeholder={placeholder}
          invalid={!!error}
          disabled={busy}
        />
      </FormField>
    </ActionDialog>
  );
};

/* ---------------------------- shortfall modal ---------------------------- */

const SHORTFALL_COLUMNS: TableDataColumn<ShortfallRow>[] = [
  {
    key: 'sku',
    title: 'SKU',
    render: (_v, r) => <span className={codeCls}>{r.sku}</span>,
  },
  {
    key: 'requested',
    title: 'Requested',
    align: 'right',
    render: (_v, r) => <span className={numCls}>{fmtQty(r.requested)}</span>,
  },
  {
    key: 'available',
    title: 'Available',
    align: 'right',
    render: (_v, r) => <span className={numCls}>{fmtQty(r.available)}</span>,
  },
  {
    key: 'shortfall',
    title: 'Shortfall',
    align: 'right',
    render: (_v, r) => (
      <span className="font-mono text-[12px] font-semibold tabular-nums text-(--state-error-base)">
        {fmtQty(r.shortfall)}
      </span>
    ),
  },
];

interface ShortfallModalProps {
  open: boolean;
  orderCode: string;
  rows: ShortfallRow[];
  message?: string;
  /** Replaces the default "<code> · picking order tidak dibuat". */
  subtitle?: string;
  onClose: () => void;
  /** Offers the "Buka inbound request" shortcut; left out, there is none. */
  onOpenInbound?: () => void;
}

/** INSUFFICIENT_STOCK — nothing reserved (all-or-nothing). */
export const ShortfallModal: React.FC<ShortfallModalProps> = ({
  open,
  orderCode,
  rows,
  message,
  subtitle,
  onClose,
  onOpenInbound,
}) => (
  <Modal
    isOpen={open}
    onClose={onClose}
    icon="ri-error-warning-line"
    iconTone="danger"
    title="Stok tidak cukup"
    subtitle={subtitle ?? `${orderCode} · picking order tidak dibuat`}
    width="lg"
    footer={
      <div className="flex items-center justify-end gap-2">
        <Button tone="neutral" style="stroke" onClick={onClose}>
          Tutup
        </Button>
        {onOpenInbound && (
          <Button
            tone="neutral"
            style="stroke"
            onClick={onOpenInbound}
            leftIcon={<i className="ri-inbox-archive-line text-[14px]" />}
          >
            Buka inbound request
          </Button>
        )}
      </div>
    }
  >
    {rows.length ? (
      <TableData size="sm" data={rows} columns={SHORTFALL_COLUMNS} />
    ) : (
      <p className="text-sm text-text-sub-600">{message}</p>
    )}
  </Modal>
);
